import { useState } from 'react'
import { useLoadedManta, useRefreshingQuery } from '@happy-ops-org/manta-react'
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LockIcon from '@mui/icons-material/Lock'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import { CollectionMetaModel, collectionMetaDef } from './manta.js'
import { CollectionForm } from './CollectionForm.js'
import { ImportPage } from './ImportPage.js'
import type { CollectionAnnotation } from './types.js'

type View = { type: 'collection'; lid: string } | { type: 'import' } | null

export const CollectionEditor = () => {
  const manta = useLoadedManta()
  const { state, data } = useRefreshingQuery(
    CollectionMetaModel,
    {},
    { debounceMs: 200 },
  )
  const [view, setView] = useState<View>(null)

  if (state === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    )
  }

  const selectedLid = view?.type === 'collection' ? view.lid : null
  const selectedEntry = data?.find((d) => d.lid === selectedLid)

  const save = async (lid: string, annotation: CollectionAnnotation) => {
    await manta.updateOrCreate(collectionMetaDef, lid, annotation)
  }

  const archiveCollection = async (lid: string) => {
    await manta.archive(collectionMetaDef, lid, true)
    setView(null)
  }

  const addNew = () => {
    const name = prompt('Collection Name (= tableName):')
    if (name?.trim()) setView({ type: 'collection', lid: name.trim() })
  }

  const handleDownload = () => {
    const definitions = Object.fromEntries(
      (data ?? []).map((entry) => [entry.lid, entry.data]),
    )
    const blob = new Blob([JSON.stringify(definitions, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'mcp-collection-meta.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Box display="flex" height="100%" overflow="hidden">
      {/* Sidebar */}
      <Box
        width={280}
        borderRight="1px solid"
        borderColor="divider"
        display="flex"
        flexDirection="column"
        bgcolor="grey.50"
      >
        <Box
          px={2}
          py={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Collections
          </Typography>
          <Box display="flex" gap={0.5}>
            <Tooltip title="Alle exportieren">
              <Button
                size="small"
                onClick={handleDownload}
                sx={{ minWidth: 0, px: 1 }}
              >
                <DownloadIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="JSON importieren">
              <Button
                size="small"
                onClick={() => setView({ type: 'import' })}
                sx={{ minWidth: 0, px: 1 }}
                color={view?.type === 'import' ? 'primary' : 'inherit'}
              >
                <UploadIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Button size="small" startIcon={<AddIcon />} onClick={addNew}>
              Neu
            </Button>
          </Box>
        </Box>

        <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
          {data?.map((entry) => (
            <Box key={entry.lid}>
              <ListItemButton
                selected={selectedLid === entry.lid}
                onClick={() =>
                  setView({ type: 'collection', lid: entry.lid ?? '' })
                }
              >
                <ListItemText
                  primary={entry.lid}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                  secondary={
                    entry.data.description
                      ? entry.data.description.slice(0, 50) +
                        (entry.data.description.length > 50 ? '…' : '')
                      : undefined
                  }
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
                {entry.data.readOnly && (
                  <ListItemSecondaryAction>
                    <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </ListItemSecondaryAction>
                )}
              </ListItemButton>
              <Divider />
            </Box>
          ))}
        </List>
      </Box>

      {/* Main Area */}
      <Box flex={1} overflow="auto">
        {view?.type === 'collection' && (
          <CollectionForm
            key={view.lid}
            lid={view.lid}
            initial={selectedEntry?.data ?? { description: '' }}
            onSave={(annotation) => save(view.lid, annotation)}
            onArchive={
              selectedEntry ? () => archiveCollection(view.lid) : undefined
            }
          />
        )}
        {view?.type === 'import' && <ImportPage />}
        {view === null && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            color="text.disabled"
          >
            <Typography>
              Collection wählen, neu anlegen oder JSON importieren.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
